import { sql } from "drizzle-orm";
import {
  check,
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { user } from "./auth";
import { hiive } from "./hiive";
import type { Rule } from "./wallet-card";

// ============================================================================
// ENUMS
// ============================================================================

// Where a promotion is anchored. This is the "scope" axis — distinct from
// eligibility (who qualifies), which is handled by the reused Rule engine.
export const PROMOTION_SCOPE_LEVEL = [
  "platform",
  "hiive",
  "event",
  "venue",
  "tier",
] as const;
export type PromotionScopeLevel = (typeof PROMOTION_SCOPE_LEVEL)[number];

export const PROMOTION_DISCOUNT_TYPE = ["percentage", "fixed"] as const;
export type PromotionDiscountType = (typeof PROMOTION_DISCOUNT_TYPE)[number];

// Which cart types a promotion can apply to. "all" = any of the below.
export const PROMOTION_APPLIES_TO = [
  "event",
  "venue",
  "membership",
  "all",
] as const;
export type PromotionAppliesTo = (typeof PROMOTION_APPLIES_TO)[number];

// How a promotion is surfaced/applied.
//   auto_apply  → no code, applied automatically when eligible
//   public      → shared code, discoverable in the offers list
//   private     → shared code, only via direct entry / deep link
//   single_use  → unique minted codes, each redeemable once
export const PROMOTION_DISCOVERABILITY = [
  "public",
  "private",
  "auto_apply",
  "single_use",
] as const;
export type PromotionDiscoverability =
  (typeof PROMOTION_DISCOVERABILITY)[number];

export const PROMOTION_FUNDING_SOURCE = [
  "platform",
  "hiive",
  "shared",
] as const;
export type PromotionFundingSource = (typeof PROMOTION_FUNDING_SOURCE)[number];

// Intent state. The *effective* availability (within schedule window) is
// computed at query time — there is no scheduler cron.
export const PROMOTION_STATUS = [
  "draft",
  "active",
  "paused",
  "archived",
] as const;
export type PromotionStatus = (typeof PROMOTION_STATUS)[number];

export const PROMOTION_ACTOR_ROLE = ["queen", "platform_admin"] as const;
export type PromotionActorRole = (typeof PROMOTION_ACTOR_ROLE)[number];

export const PROMOTION_AUDIT_ACTION = [
  "create",
  "update",
  "activate",
  "pause",
  "archive",
  "delete",
  "code_minted",
] as const;
export type PromotionAuditAction = (typeof PROMOTION_AUDIT_ACTION)[number];

// ============================================================================
// JSON SHAPES
// ============================================================================

// Product-level restrictions. Vocabulary mirrors venueExperiencePriceRule so
// venue constraints feel native. Only the fields relevant to a promo's
// appliesTo are honored by the engine.
export type PromotionConstraints = {
  // Venue
  daysOfWeek?: number[]; // 0 (Sun) - 6 (Sat)
  timeWindows?: Array<{ startMinute: number; endMinute: number }>;
  minDurationMinutes?: number;
  maxDurationMinutes?: number;
  dateRange?: { from?: string | null; to?: string | null }; // ISO dates
  venueExperienceIds?: number[];
  venueSpotIds?: number[];
  // Event
  eventPassTypeIds?: number[];
  eventTierIds?: number[];
  // Membership
  billingIntervals?: string[]; // month | quarter | half_year | year | lifetime
};

// One applied promotion, snapshotted onto the booking/membership at reserve.
export type AppliedPromotion = {
  promotionId: number;
  codeId: number | null;
  code: string | null;
  discountType: PromotionDiscountType;
  discountValue: number;
  amountMinor: number;
  fundingSource: PromotionFundingSource;
  platformFundedMinor: number;
  hiiveFundedMinor: number;
  precedence: number;
  scopeLevel: PromotionScopeLevel;
};

export type ManualBookingDiscountSnapshot = {
  type: "manual";
  mode: "flat" | "percent" | "comp";
  value: number;
  amountMinor: number;
  reason: string;
};

// Immutable record of the resolved discount, written at reserve and used for
// reconciliation, payouts, refunds, and audit. Stored on each booking row.
export type PromotionDiscountSnapshot = {
  applied: AppliedPromotion[];
  subtotalMinor: number;
  totalDiscountMinor: number;
  currency: string;
  paymentFingerprint?: string | null;
  resolvedAt: string; // ISO
  // Front-desk adjustment applied after any promotion. Kept separate from the
  // promotion array so promo funding/analytics never mistake it for a campaign.
  manual?: ManualBookingDiscountSnapshot;
  // Hiive Money (platform credits) applied on the residual after promo + manual.
  // Platform-funded by definition: the payout calculator adds it to the platform
  // hit and never subtracts it from the operator's base. Kept as its own field so
  // credit cost stays separable from promo cost in reporting.
  creditFundedMinor?: number;
};

// ============================================================================
// PROMOTION - the campaign config (single source of truth for a promo's rules)
// ============================================================================
export const promotion = sqliteTable(
  "promotion",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    scopeLevel: text("scope_level").notNull().$type<PromotionScopeLevel>(),
    // Polymorphic: hiiveId / eventId / venueId / tierId depending on scopeLevel.
    // Null only for platform scope. No FK (polymorphic by design).
    scopeId: integer("scope_id"),
    // Owning/target hiive. Null = platform-wide. Stable indexed key for
    // candidate gathering ("platform promos OR this hiive's promos").
    hiiveId: integer("hiive_id").references(() => hiive.id, {
      onDelete: "cascade",
    }),

    name: text("name").notNull(),
    description: text("description"),

    discountType: text("discount_type")
      .notNull()
      .$type<PromotionDiscountType>(),
    // percentage: whole percent (e.g. 10). fixed: minor units (e.g. 10000).
    discountValue: integer("discount_value").notNull(),
    maxDiscountCapMinor: integer("max_discount_cap_minor"),
    minBookingValueMinor: integer("min_booking_value_minor"),
    currency: text("currency").notNull().default("INR"),

    appliesTo: text("applies_to")
      .notNull()
      .$type<PromotionAppliesTo>()
      .default("all"),
    discoverability: text("discoverability")
      .notNull()
      .$type<PromotionDiscoverability>()
      .default("auto_apply"),

    stackable: integer("stackable", { mode: "boolean" })
      .notNull()
      .default(false),
    precedence: integer("precedence").notNull().default(0),

    fundingSource: text("funding_source")
      .notNull()
      .$type<PromotionFundingSource>()
      .default("hiive"),
    // For shared funding: percent of each discount the platform absorbs (0-100).
    platformFundedPct: integer("platform_funded_pct"),

    maxTotalRedemptions: integer("max_total_redemptions"),
    // null = no per-user cap (standing/auto promos). Creation forms default this
    // to 1 for code-based promos and leave it null for auto_apply.
    maxPerUser: integer("max_per_user"),
    perDeviceLimit: integer("per_device_limit"),
    perPaymentMethodLimit: integer("per_payment_method_limit"),

    startsAt: integer("starts_at", { mode: "timestamp" }),
    endsAt: integer("ends_at", { mode: "timestamp" }),

    status: text("status").notNull().$type<PromotionStatus>().default("draft"),
    restoreOnCancel: integer("restore_on_cancel", { mode: "boolean" })
      .notNull()
      .default(true),

    eligibilityRule: text("eligibility_rule", { mode: "json" })
      .$type<Rule>()
      .notNull()
      .default({ type: "always" }),
    constraints: text("constraints", { mode: "json" })
      .$type<PromotionConstraints>()
      .notNull()
      .default({}),

    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    createdByRole: text("created_by_role").$type<PromotionActorRole>(),

    ...timestamps,
  },
  (t) => [
    index("promotion_by_hiive_status").on(t.hiiveId, t.status),
    index("promotion_by_scope").on(t.scopeLevel, t.scopeId),
    index("promotion_by_status_window").on(t.status, t.startsAt, t.endsAt),
    check(
      "promotion_scope_id_check",
      sql`(${t.scopeLevel} = 'platform' AND ${t.scopeId} IS NULL) OR (${t.scopeLevel} != 'platform' AND ${t.scopeId} IS NOT NULL)`,
    ),
    check("promotion_discount_value_check", sql`${t.discountValue} >= 0`),
    check(
      "promotion_percentage_range_check",
      sql`${t.discountType} != 'percentage' OR (${t.discountValue} >= 0 AND ${t.discountValue} <= 100)`,
    ),
    check(
      "promotion_platform_funded_pct_check",
      sql`${t.platformFundedPct} IS NULL OR (${t.platformFundedPct} >= 0 AND ${t.platformFundedPct} <= 100)`,
    ),
  ],
);

// ============================================================================
// PROMOTION_CODE - 0..N redeemable codes per promotion
//   0 codes     → auto_apply
//   1 code      → public / private shared code
//   N codes     → unique single-use codes (one redemption each)
// ============================================================================
export const promotionCode = sqliteTable(
  "promotion_code",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    promotionId: integer("promotion_id")
      .notNull()
      .references(() => promotion.id, { onDelete: "cascade" }),

    code: text("code").notNull(),
    // null = unlimited shared; 1 = single-use; N = capped shared.
    maxRedemptions: integer("max_redemptions"),
    // Materialized claim counter and concurrency CAS anchor. Incremented when a
    // code is held at reserve, decremented on release. Rebuildable from bookings.
    timesRedeemed: integer("times_redeemed").notNull().default(0),
    // For minted single-use codes assigned to a specific user.
    assignedUserId: text("assigned_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    redeemedAt: integer("redeemed_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("promotion_code_code_unique").on(t.code),
    index("promotion_code_by_promotion").on(t.promotionId),
    check("promotion_code_times_redeemed_check", sql`${t.timesRedeemed} >= 0`),
  ],
);

// ============================================================================
// PROMOTION_AUDIT_LOG - append-only config-mutation audit.
// Redemption audit is NOT here — it is derived from booking lifecycle.
// ============================================================================
export const promotionAuditLog = sqliteTable(
  "promotion_audit_log",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    promotionId: integer("promotion_id").references(() => promotion.id, {
      onDelete: "set null",
    }),
    promotionCodeId: integer("promotion_code_id"),
    actorUserId: text("actor_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    actorRole: text("actor_role").$type<PromotionActorRole>(),
    action: text("action").notNull().$type<PromotionAuditAction>(),
    beforeJson: text("before_json", { mode: "json" }).$type<
      Record<string, unknown>
    >(),
    afterJson: text("after_json", { mode: "json" }).$type<
      Record<string, unknown>
    >(),
    hiiveId: integer("hiive_id"),
    ...timestamps,
  },
  (t) => [
    index("promotion_audit_by_promotion").on(t.promotionId),
    index("promotion_audit_by_actor").on(t.actorUserId),
    index("promotion_audit_by_hiive").on(t.hiiveId),
  ],
);

// ============================================================================
// TYPE EXPORTS
// ============================================================================
export type Promotion = typeof promotion.$inferSelect;
export type PromotionInsert = typeof promotion.$inferInsert;
export type PromotionCode = typeof promotionCode.$inferSelect;
export type PromotionCodeInsert = typeof promotionCode.$inferInsert;
export type PromotionAuditLog = typeof promotionAuditLog.$inferSelect;
export type PromotionAuditLogInsert = typeof promotionAuditLog.$inferInsert;
